import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Spinner,
  Input,
  Textarea,
} from "@nextui-org/react";
import axios from "axios";
import { athletes_uri, orgs_uri } from "../../../../utils/url";
import { toast } from "react-toastify";
import { MdBusiness, MdPerson, MdPhotoCamera, MdNotes } from "react-icons/md";

const emptyForm = () => ({
  lastname: "",
  username: "",
  club: "",
  birth_date: "",
  weight: "",
  biography: "",
  profile_img: "",
  org_id: "",
});

export default function AthleteAdd({ callback }) {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [load, setLoad] = useState(false);
  const [orgsLoad, setOrgsLoad] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [data, setData] = useState(() => emptyForm());

  const previewUrl = useMemo(() => {
    if (data.profile_img && typeof data.profile_img !== "string") {
      return URL.createObjectURL(data.profile_img);
    }
    return null;
  }, [data.profile_img]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setOrgsLoad(true);
      try {
        const res = await axios.get(
          `${orgs_uri}?username=&page=1&limit=500`
        );
        if (!cancelled) setOrgs(res.data.data || []);
      } catch (e) {
        if (!cancelled) {
          toast.error("Байгууллагууд ачааллаж чадсангүй");
          setOrgs([]);
        }
      } finally {
        if (!cancelled) setOrgsLoad(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handlecall = () => {
    callback();
  };

  const handleUpload = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Файлын хэмжээ 5MB-с бага байх ёстой!");
      return;
    }
    if (
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png"
    ) {
      setData((d) => ({ ...d, profile_img: file }));
    } else {
      toast.error("Зөвхөн jpeg/png өргөтгөлтэй зураг оруулах боломжтой !");
    }
  };

  const onOrgChange = (orgId) => {
    const org = orgs.find((o) => String(o.id) === String(orgId));
    setData((d) => ({
      ...d,
      org_id: orgId,
      club: org ? (org.name || d.club) : d.club,
    }));
  };

  const handleSubmit = async () => {
    if (!data.org_id) {
      toast.error("Байгууллага сонгоно уу");
      return;
    }
    if (!data.lastname?.trim() || !data.username?.trim()) {
      toast.error("Овог, нэр заавал бөглөнө үү");
      return;
    }
    if (!data.profile_img) {
      toast.error("Зураг оруулна уу");
      return;
    }
    if (!data.club?.trim()) {
      toast.error("Клуб / байгууллагын нэр оруулна уу");
      return;
    }

    setLoad(true);
    const form = new FormData();
    form.append("profile_img", data.profile_img);
    form.append("lastname", data.lastname.trim());
    form.append("username", data.username.trim());
    form.append("club", data.club.trim());
    form.append("birth_date", data.birth_date);
    form.append("weight", data.weight);
    form.append("biography", data.biography);
    form.append("org_id", String(data.org_id));

    try {
      const res = await axios.post(`${athletes_uri}/admin/create`, form);
      if (res.status === 200) {
        toast.success("Тамирчныг нэмлээ !");
        setData(emptyForm());
        onOpenChange(false);
        handlecall();
      }
    } catch (err) {
      console.log(err);
      toast.error("Хадгалахад алдаа гарлаа");
    } finally {
      setLoad(false);
    }
  };

  const fieldClass =
    "rounded-xl border border-slate-200 bg-white shadow-sm transition-colors hover:border-slate-300";

  return (
    <>
      <Button
        className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 font-medium text-white shadow-md"
        onPress={onOpen}
      >
        Тамирчин нэмэх
      </Button>
      <Modal
        size="3xl"
        scrollBehavior="inside"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        classNames={{ backdrop: "bg-black/40" }}
      >
        <ModalContent className="border border-slate-100">
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white py-5">
                <span className="text-lg font-semibold text-slate-800">
                  Шинэ тамирчин
                </span>
                <span className="text-sm font-normal text-slate-500">
                  Бүртгэлтэй байгууллага сонгож, мэдээллийг бөглөнө үү
                </span>
              </ModalHeader>
              <ModalBody className="gap-0 py-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-5 rounded-2xl bg-slate-50/90 p-4 ring-1 ring-slate-100 md:p-5">
                    <div className="flex items-center gap-2 text-slate-700">
                      <MdBusiness className="text-xl text-blue-600" />
                      <h2 className="text-sm font-semibold uppercase tracking-wide">
                        Байгууллага
                      </h2>
                    </div>
                    {orgsLoad ? (
                      <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
                        <Spinner size="sm" />
                        <span className="text-sm">Ачааллаж байна...</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Бүртгэлтэй байгууллага сонгох *
                        </label>
                        <select
                          className={`${fieldClass} w-full px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20`}
                          value={data.org_id}
                          onChange={(e) => onOrgChange(e.target.value)}
                        >
                          <option value="">— Сонгох —</option>
                          {orgs.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name || "Нэргүй"}
                              {o.short_name ? ` (${o.short_name})` : ""}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-500">
                          Сонгосны дараа &quot;Клуб / төлөөлж буй нэр&quot; талбар
                          автоматаар бөглөгдөнө.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-slate-600">
                        Клуб / төлөөлж буй нэр *
                      </label>
                      <Input
                        value={data.club}
                        onChange={(e) =>
                          setData({ ...data, club: e.target.value })
                        }
                        size="sm"
                        placeholder="Жишээ: Алдар спорт хороо"
                        classNames={{
                          inputWrapper: `${fieldClass} shadow-none`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-5 rounded-2xl bg-slate-50/90 p-4 ring-1 ring-slate-100 md:p-5">
                    <div className="flex items-center gap-2 text-slate-700">
                      <MdPerson className="text-xl text-blue-600" />
                      <h2 className="text-sm font-semibold uppercase tracking-wide">
                        Тамирчин
                      </h2>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Овог *
                        </label>
                        <Input
                          value={data.lastname}
                          onChange={(e) =>
                            setData({ ...data, lastname: e.target.value })
                          }
                          size="sm"
                          placeholder="Овог"
                          classNames={{
                            inputWrapper: `${fieldClass} shadow-none`,
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Нэр *
                        </label>
                        <Input
                          value={data.username}
                          onChange={(e) =>
                            setData({ ...data, username: e.target.value })
                          }
                          size="sm"
                          placeholder="Нэр"
                          classNames={{
                            inputWrapper: `${fieldClass} shadow-none`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Төрсөн огноо
                        </label>
                        <Input
                          type="date"
                          value={data.birth_date}
                          onChange={(e) =>
                            setData({ ...data, birth_date: e.target.value })
                          }
                          size="sm"
                          classNames={{
                            inputWrapper: `${fieldClass} shadow-none`,
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Жин
                        </label>
                        <Input
                          value={data.weight}
                          onChange={(e) =>
                            setData({ ...data, weight: e.target.value })
                          }
                          size="sm"
                          placeholder="кг"
                          classNames={{
                            inputWrapper: `${fieldClass} shadow-none`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  <div className="space-y-3 rounded-2xl bg-slate-50/90 p-4 ring-1 ring-slate-100 md:p-5">
                    <div className="flex items-center gap-2 text-slate-700">
                      <MdPhotoCamera className="text-xl text-blue-600" />
                      <h2 className="text-sm font-semibold uppercase tracking-wide">
                        Профайл зураг
                      </h2>
                    </div>
                    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                      <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-200 bg-white">
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="px-2 text-center text-xs text-slate-400">
                            Зураг сонгох
                          </span>
                        )}
                      </div>
                      <div className="w-full flex-1 space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Зураг оруулах * (jpeg/png, 5MB хүртэл)
                        </label>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg"
                          className={`${fieldClass} block w-full cursor-pointer px-3 py-2 text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-blue-700`}
                          onChange={(e) =>
                            handleUpload(e.target.files?.[0])
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 rounded-2xl bg-slate-50/90 p-4 ring-1 ring-slate-100 md:p-5">
                    <div className="flex items-center gap-2 text-slate-700">
                      <MdNotes className="text-xl text-blue-600" />
                      <h2 className="text-sm font-semibold uppercase tracking-wide">
                        Танилцуулга
                      </h2>
                    </div>
                    <Textarea
                      value={data.biography}
                      onChange={(e) =>
                        setData({ ...data, biography: e.target.value })
                      }
                      minRows={5}
                      placeholder="Тамирчны товч танилцуулга..."
                      classNames={{
                        input: "text-sm",
                        inputWrapper: `${fieldClass} shadow-none`,
                      }}
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter className="border-t border-slate-100 bg-slate-50/50">
                <Button variant="light" color="default" onPress={onClose}>
                  Болих
                </Button>
                {load ? (
                  <Button
                    className="min-w-28 rounded-xl bg-blue-600 text-white"
                    isDisabled
                  >
                    <Spinner size="sm" color="white" />
                  </Button>
                ) : (
                  <Button
                    className="min-w-28 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 font-medium text-white shadow-md"
                    onPress={handleSubmit}
                  >
                    Нэмэх
                  </Button>
                )}
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
